import React, { useState, useEffect } from 'react';
import { savePurchase } from '../../data/purchases';

export default function PurchaseOrderForm({
  suppliers, catalogProducts, purchases, setPurchases,
  editingPurchaseId, setEditingPurchaseId, receiveForm, setReceiveForm,
  message, setMessage, onDone
}) {
  const [supplierId, setSupplierId] = useState('');
  const [items, setItems] = useState([]);
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [orderNumber, setOrderNumber] = useState('');

  useEffect(() => {
    if (editingPurchaseId) {
      const p = purchases.find(x => x.id === editingPurchaseId);
      if (p) {
        setSupplierId(p.supplierId || '');
        setItems(p.items || []);
        setExpectedDate(p.expectedDate ? p.expectedDate.substring(0, 10) : '');
        setNotes(p.notes || '');
        setStatus(p.status || 'draft');
        setOrderNumber(p.orderNumber || '');
      }
    } else {
      setSupplierId('');
      setItems([]);
      setExpectedDate('');
      setNotes('');
      setStatus('draft');
      setOrderNumber('');
    }
  }, [editingPurchaseId, purchases]);

  const handleAddItem = () => {
    setItems([...items, { productId: '', quantity: 1, unitCost: 0 }]);
  };

  const handleRemoveItem = (idx) => {
    const newItems = [...items];
    newItems.splice(idx, 1);
    setItems(newItems);
  };

  const handleItemChange = (idx, field, value) => {
    const newItems = [...items];
    if (field === 'productId') {
      newItems[idx].productId = value;
      const prod = catalogProducts.find(p => p.id === value);
      if (prod && prod.purchasePrice) {
        newItems[idx].unitCost = prod.purchasePrice;
      }
    } else {
      newItems[idx][field] = Number(value);
    }
    setItems(newItems);
  };

  const handleSaveOrder = (e) => {
    e.preventDefault();
    if (!supplierId) {
      alert("Veuillez sélectionner un fournisseur.");
      return;
    }
    if (items.length === 0 || items.some(i => !i.productId)) {
      alert("Veuillez ajouter des produits valides.");
      return;
    }

    const totalAmount = items.reduce((sum, i) => sum + (i.quantity * i.unitCost), 0);
    const purchaseData = {
      id: editingPurchaseId || Date.now().toString(),
      supplierId,
      items,
      expectedDate,
      notes,
      status,
      orderNumber: orderNumber || `BC-${Date.now()}`,
      totalAmount,
      createdAt: editingPurchaseId ? purchases.find(p => p.id === editingPurchaseId)?.createdAt : new Date().toISOString()
    };

    savePurchase(purchaseData);
    if (editingPurchaseId) {
      setPurchases(purchases.map(p => p.id === editingPurchaseId ? purchaseData : p));
      setMessage('Commande mise à jour.');
    } else {
      setPurchases([purchaseData, ...purchases]);
      setMessage('Nouvelle commande créée.');
    }
    
    setTimeout(() => setMessage(''), 3000);
    onDone();
  };

  const handleSaveReceive = (e) => {
    e.preventDefault();
    const p = purchases.find(x => x.id === receiveForm.purchaseId);
    if (!p) return;

    let allReceived = true;
    let anyReceived = false;

    const updatedItems = p.items.map((item, idx) => {
      const recQty = Number(receiveForm.items[idx].receivedQuantity);
      if (recQty > 0) anyReceived = true;
      if (recQty < item.quantity) allReceived = false;
      return { ...item, receivedQuantity: recQty };
    });

    let newStatus = p.status;
    if (allReceived) newStatus = 'received';
    else if (anyReceived) newStatus = 'partial';

    const updatedPurchase = {
      ...p,
      items: updatedItems,
      status: newStatus,
      receivedDate: new Date().toISOString()
    };

    savePurchase(updatedPurchase);
    setPurchases(purchases.map(x => x.id === p.id ? updatedPurchase : x));
    
    // Update inventory logic should ideally happen here or in data layer
    
    setMessage(`Commande marquée comme ${newStatus === 'received' ? 'reçue' : 'partiellement reçue'}.`);
    setTimeout(() => setMessage(''), 3000);
    setReceiveForm(null);
    onDone();
  };

  if (receiveForm) {
    const p = purchases.find(x => x.id === receiveForm.purchaseId);
    return (
      <div className="card shadow-sm">
        <div className="card-header bg-success text-white">
          <h5 className="mb-0">📦 Réceptionner la commande {p?.orderNumber}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSaveReceive}>
            <table className="table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Commandé</th>
                  <th>Déjà Reçu</th>
                  <th>A Recevoir</th>
                </tr>
              </thead>
              <tbody>
                {receiveForm.items.map((item, idx) => {
                  const prod = catalogProducts.find(c => c.id === item.productId);
                  const pItem = p.items[idx];
                  return (
                    <tr key={idx}>
                      <td>{prod ? prod.name : 'Inconnu'}</td>
                      <td>{pItem.quantity}</td>
                      <td>{pItem.receivedQuantity || 0}</td>
                      <td>
                        <input type="number" className="form-control form-control-sm" style={{width: '100px'}}
                          value={item.receivedQuantity}
                          onChange={e => {
                            const newItems = [...receiveForm.items];
                            newItems[idx].receivedQuantity = e.target.value;
                            setReceiveForm({ ...receiveForm, items: newItems });
                          }}
                          max={pItem.quantity} min="0" required
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-end mt-3">
              <button type="button" className="btn btn-secondary me-2" onClick={() => setReceiveForm(null)}>Annuler</button>
              <button type="submit" className="btn btn-success">Enregistrer la réception</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light">
        <h5 className="mb-0">{editingPurchaseId ? '✏️ Modifier Commande' : '➕ Nouvelle Commande'}</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSaveOrder}>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Fournisseur <span className="text-danger">*</span></label>
              <select className="form-select" value={supplierId} onChange={e => setSupplierId(e.target.value)} required>
                <option value="">Sélectionner un fournisseur...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Statut</label>
              <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="draft">Brouillon</option>
                <option value="ordered">Commandée</option>
                <option value="partial">Partielle</option>
                <option value="received">Reçue</option>
                <option value="cancelled">Annulée</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date prévue</label>
              <input type="date" className="form-control" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label">N° Commande</label>
              <input type="text" className="form-control" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="Auto" />
            </div>
          </div>

          <h6 className="mb-3">Lignes de commande</h6>
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Produit</th>
                <th>Quantité</th>
                <th>Coût Unitaire HT</th>
                <th>Total Ligne</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    <select className="form-select form-select-sm" value={item.productId} onChange={e => handleItemChange(idx, 'productId', e.target.value)} required>
                      <option value="">Sélectionner...</option>
                      {catalogProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" min="1" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', e.target.value)} required />
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" step="0.01" value={item.unitCost} onChange={e => handleItemChange(idx, 'unitCost', e.target.value)} required />
                  </td>
                  <td>{(item.quantity * item.unitCost).toFixed(2)} €</td>
                  <td className="text-center">
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveItem(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={handleAddItem}>+ Ajouter un produit</button>
          
          <div className="mb-3">
            <label className="form-label">Notes / Instructions</label>
            <textarea className="form-control" rows="2" value={notes} onChange={e => setNotes(e.target.value)}></textarea>
          </div>
          
          <div className="d-flex justify-content-between align-items-center mt-4">
            <h4>Total: {items.reduce((sum, i) => sum + (i.quantity * i.unitCost), 0).toFixed(2)} €</h4>
            <div>
              <button type="button" className="btn btn-secondary me-2" onClick={onDone}>Annuler</button>
              <button type="submit" className="btn btn-primary">Enregistrer la commande</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
